import { useState } from 'react'
import usePersistedFilters from '../../../../hooks/usePersistedFilters'
import useFetchData from '../../../../hooks/UseFetchData'
import Table from '../../../../components/his/invoices/Table'
import PageHeader from '../../../../shared/PageHeader'
import {
  FaArrowCircleLeft,
  FaArrowCircleRight,
  FaFileExcel,
  FaFilePdf,
} from 'react-icons/fa'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import XLSX from 'xlsx-js-style'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'

import { groupPayers } from '../../../../utils/GroupByItem'
import PayerReportFilters from './PayerReportFilters'
import moment from 'moment'
import CustomButton from '../../../../shared/CustomButton'
import { AmiriRegBase64 } from '../../../../assets/fonts/Amiri'

export const columns = t => [
  {
    header: t('PayerName'),
    headerCellClass: '!bg-gray-200',
    bodyCellClass: 'border !border-gray-300',
    body: row =>
      row?.isTotal ? (
        <span className="font-bold">{t('Total')}</span>
      ) : row?.isSaperator ? (
        ''
      ) : (
        row?.PayerName || ''
      ),
  },
  {
    header: t('Payer Category'),
    headerCellClass: '!bg-gray-200',
    bodyCellClass: 'border !border-gray-300',
    body: row =>
      row?.isTotal || row?.isSaperator ? (
        ''
      ) : (
        row?.PayerCategory || '-'
      ),
  },
  {
    header: t('Number of Claims'),
    headerCellClass: '!bg-gray-200',
    bodyCellClass: 'border !border-gray-300 text-right',
    body: row =>
      row?.isSaperator ? (
        ''
      ) : (
        <span className={row?.isTotal ? 'font-bold' : ''}>
          {Number(row?.NumberOfClaims || 0)}
        </span>
      ),
  },
  {
    header: t('Gross'),
    headerCellClass: '!bg-gray-200',
    bodyCellClass: 'border !border-gray-300 text-right',
    body: row =>
      row?.isSaperator ? (
        ''
      ) : (
        <span className={row?.isTotal ? 'font-bold' : ''}>
          {Number(row?.TotalGross || 0).toFixed(2)}
        </span>
      ),
  },
  {
    header: t('Discount'),
    headerCellClass: '!bg-gray-200',
    bodyCellClass: 'border !border-gray-300 text-right',
    body: row =>
      row?.isSaperator ? (
        ''
      ) : (
        <span className={row?.isTotal ? 'font-bold' : ''}>
          {Number(row?.TotalDiscount || 0).toFixed(2)}
        </span>
      ),
  },
  {
    header: t('Net'),
    headerCellClass: '!bg-gray-200',
    bodyCellClass: 'border !border-gray-300 text-right',
    body: row =>
      row?.isSaperator ? (
        ''
      ) : (
        <span className={row?.isTotal ? 'font-bold' : ''}>
          {Number(row?.Net || 0).toFixed(2)}
        </span>
      ),
  },
  {
    header: t('Patient Share'),
    headerCellClass: '!bg-gray-200',
    bodyCellClass: 'border !border-gray-300 text-right',
    body: row =>
      row?.isSaperator ? (
        ''
      ) : (
        <span className={row?.isTotal ? 'font-bold' : ''}>
          {Number(row?.TotalPatientShare || 0).toFixed(2)}
        </span>
      ),
  },
  {
    header: t('Payer Net'),
    headerCellClass: '!bg-gray-200',
    bodyCellClass: 'border !border-gray-300 text-right',
    body: row =>
      row?.isSaperator ? (
        ''
      ) : (
        <span className={row?.isTotal ? 'font-bold' : ''}>
          {Number(row?.TotalPayorNet || 0).toFixed(2)}
        </span>
      ),
  },
  {
    header: t('Payer VAT'),
    headerCellClass: '!bg-gray-200',
    bodyCellClass: 'border !border-gray-300 text-right',
    body: row =>
      row?.isSaperator ? (
        ''
      ) : (
        <span className={row?.isTotal ? 'font-bold' : ''}>
          {Number(row?.TotalPayorVAT || 0).toFixed(2)}
        </span>
      ),
  },
  {
    header: t('Payer Share'),
    headerCellClass: '!bg-gray-200',
    bodyCellClass: 'border !border-gray-300 text-right',
    body: row =>
      row?.isSaperator ? (
        ''
      ) : (
        <span className={row?.isTotal ? 'font-bold' : ''}>
          {Number(row?.TotalPayorShare || 0).toFixed(2)}
        </span>
      ),
  },
  {
    header: t('Claim Total'),
    headerCellClass: '!bg-gray-200',
    bodyCellClass: 'border !border-gray-300 text-right',
    body: row =>
      row?.isSaperator ? (
        ''
      ) : (
        <span className={row?.isTotal ? 'font-bold' : ''}>
          {Number(row?.TotalClaim || 0).toFixed(2)}
        </span>
      ),
  },
]

export function exportToExcel(data, t, fileName = 'payer-summary.xlsx') {
  if (!data || !Array.isArray(data)) return

  const formatted = data
    .filter(row => !row.isSaperator)
    .map(row => {
      if (row.isTotal) {
        return {
          [t('Payer Name')]: t('Total'),
          [t('Payer Category')]: '',
          [t('Number of Claims')]: row.NumberOfClaims || 0,
          [t('Gross')]: Number(row.TotalGross || 0).toFixed(2),
          [t('Discount')]: Number(row.TotalDiscount || 0).toFixed(2),
          [t('Net')]: Number(row.Net || 0).toFixed(2),
          [t('Patient Share')]: Number(row.TotalPatientShare || 0).toFixed(2),
          [t('Payer Net')]: Number(row.TotalPayorNet || 0).toFixed(2),
          [t('Payer VAT')]: Number(row.TotalPayorVAT || 0).toFixed(2),
          [t('Payer Share')]: Number(row.TotalPayorShare || 0).toFixed(2),
          [t('Claim Total')]: Number(row.TotalClaim || 0).toFixed(2),
        }
      }

      return {
        [t('Payer Name')]: row.PayerName || '',
        [t('Payer Category')]: row.PayerCategory || '-',
        [t('Number of Claims')]: row.NumberOfClaims || 0,
        [t('Gross')]: Number(row.TotalGross || row.Gross || 0).toFixed(2),
        [t('Discount')]: Number(row.TotalDiscount || row.Discount || 0).toFixed(2),
        [t('Net')]: Number(row.Net || 0).toFixed(2),
        [t('Patient Share')]: Number(row.TotalPatientShare || row.PatientShare || 0).toFixed(2),
        [t('Payer Net')]: Number(row.TotalPayorNet || row.PayorNet || 0).toFixed(2),
        [t('Payer VAT')]: Number(row.TotalPayorVAT || row.PayorVAT || 0).toFixed(2),
        [t('Payer Share')]: Number(row.TotalPayorShare || row.PayorShare || 0).toFixed(2),
        [t('Claim Total')]: Number(row.TotalClaim || row.ClaimTotal || 0).toFixed(2),
      }
    })

  const ws = XLSX.utils.json_to_sheet(formatted)
  const range = XLSX.utils.decode_range(ws['!ref'])

  const headerStyle = {
    fill: { patternType: 'solid', fgColor: { rgb: '1A365D' } },
    font: { color: { rgb: 'FFFFFF' }, bold: true, sz: 11, name: 'Calibri' },
    alignment: { horizontal: 'center', vertical: 'center' },
    border: {
      top: { style: 'thin', color: { rgb: '000000' } },
      bottom: { style: 'thin', color: { rgb: '000000' } },
    },
  }

  const summaryStyle = {
    fill: { patternType: 'solid', fgColor: { rgb: 'D6EAF8' } },
    font: { bold: true, color: { rgb: '0D47A1' }, sz: 10, name: 'Calibri' },
    alignment: { horizontal: 'center', vertical: 'center' },
  }

  for (let C = range.s.c; C <= range.e.c; ++C) {
    const headerAddress = XLSX.utils.encode_col(C) + '1'
    if (ws[headerAddress]) ws[headerAddress].s = headerStyle

    for (let R = range.s.r + 1; R <= range.e.r; ++R) {
      const cellAddress = XLSX.utils.encode_cell({ r: R, c: C })
      if (!ws[cellAddress]) continue
      const rowData = data.filter(r => !r.isSaperator)[R - 1]
      if (rowData?.isTotal) ws[cellAddress].s = summaryStyle
    }
  }

  ws['!cols'] = Object.keys(formatted[0] || {}).map(() => ({ wch: 18 }))
  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, 'Payer Summary')
  XLSX.writeFile(wb, fileName)
}

export function exportToPDF(data, t, payload, fileName = 'payer-summary.pdf') {
  const doc = new jsPDF('landscape')

  doc.addFileToVFS('Amiri-Regular.ttf', AmiriRegBase64)
  doc.addFont('Amiri-Regular.ttf', 'Amiri', 'normal')
  doc.setFont('Amiri', 'normal')

  const PAGE_WIDTH = doc.internal.pageSize.getWidth()
  let y = 12

  if (payload.logo) {
    const props = doc.getImageProperties(payload.logo)
    let w = props.width
    let h = props.height
    const scale = Math.min(25 / w, 25 / h)
    doc.addImage(payload.logo, 'PNG', 15, 5, w * scale, h * scale)
  }

  doc.setFontSize(12)
  doc.text(payload.business_en || 'Organization', 45, 12)

  doc.setFontSize(10)
  doc.text(payload.address || '-', 45, 18)

  doc.setFontSize(12)
  doc.text(t('Payer Summary Report'), PAGE_WIDTH - 14, 12, { align: 'right' })

  doc.setFontSize(10)
  doc.text(
    `${t('Printed On')}: ${moment().format('DD/MM/YYYY')}`,
    PAGE_WIDTH - 14,
    18,
    { align: 'right' }
  )

  y = 30

  const headers = [
    t('Payer Name'),
    t('Payer Category'),
    t('Number of Claims'),
    t('Gross'),
    t('Discount'),
    t('Net'),
    t('Patient Share'),
    t('Payer Net'),
    t('Payer VAT'),
    t('Payer Share'),
    t('Claim Total'),
  ]

  const body = []

  data.forEach(row => {
    if (row.isSaperator) return

    if (row.isTotal) {
      body.push([
        { content: t('Total'), styles: { fontStyle: 'bold' } },
        '',
        row.NumberOfClaims || 0,
        Number(row.TotalGross || 0).toFixed(2),
        Number(row.TotalDiscount || 0).toFixed(2),
        Number(row.Net || 0).toFixed(2),
        Number(row.TotalPatientShare || 0).toFixed(2),
        Number(row.TotalPayorNet || 0).toFixed(2),
        Number(row.TotalPayorVAT || 0).toFixed(2),
        Number(row.TotalPayorShare || 0).toFixed(2),
        Number(row.TotalClaim || 0).toFixed(2),
      ])
      return
    }

    body.push([
      row.PayerName || '',
      row.PayerCategory || '-',
      row.NumberOfClaims || 0,
      Number(row.TotalGross || row.Gross || 0).toFixed(2),
      Number(row.TotalDiscount || row.Discount || 0).toFixed(2),
      Number(row.Net || 0).toFixed(2),
      Number(row.TotalPatientShare || row.PatientShare || 0).toFixed(2),
      Number(row.TotalPayorNet || row.PayorNet || 0).toFixed(2),
      Number(row.TotalPayorVAT || row.PayorVAT || 0).toFixed(2),
      Number(row.TotalPayorShare || row.PayorShare || 0).toFixed(2),
      Number(row.TotalClaim || row.ClaimTotal || 0).toFixed(2),
    ])
  })

  autoTable(doc, {
    startY: y,
    head: [headers],
    body,
    theme: 'grid',
    margin: { left: 10, right: 10 },
    styles: {
      font: 'Amiri',
      fontSize: 8.5, // Much better readability for 11 columns
      cellPadding: 2,
      lineColor: [189, 195, 199],
      lineWidth: 0.1,
    },
    headStyles: {
      fillColor: [26, 54, 93], // Navy standard
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      halign: 'center',
    },
    alternateRowStyles: {
      fillColor: [248, 249, 250],
    },
    willDrawCell: data => {
      const rowText = data.cell.text?.[0]
      if (rowText === t('Total')) {
        data.cell.styles.fillColor = [214, 234, 248]
        data.cell.styles.textColor = [13, 71, 161]
        data.cell.styles.fontStyle = 'bold'
      }
    },
    didParseCell: function (hookData) {
      if (hookData.section === 'body') {
        // BOLD NUMERIC COLUMNS (Column index >= 2: Claims, Gross, Net, etc.)
        if (hookData.column.index >= 2) {
          hookData.cell.styles.fontStyle = 'bold'
        }
      }
    },
    didDrawPage: data => {
      const pageCount = doc.internal.getNumberOfPages()
      doc.setFontSize(7)
      doc.setTextColor(150)
      doc.text(
        `Page ${doc.internal.getCurrentPageInfo().pageNumber} of ${pageCount}`,
        data.settings.margin.left,
        doc.internal.pageSize.height - 8
      )
    },
  })

  doc.save(fileName)
}

const PayerReport = () => {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const auth = useSelector(state => state.auth)
  const {
    data: requestData,
    executeFetch: requestFetch,
    loading,
  } = useFetchData()

  const { filters: payloadData, setFilters: setPayloadData } = usePersistedFilters(
    'payer_report',
    {
      start_date: moment().subtract(1, 'day').toDate(),
    end_date: moment().toDate(),
    payer_id: '',
    }
  )

  const requestReport = async (payload = payloadData) => {
    await requestFetch('POST', '/v3/reports/payer', {
      ...payload,
      start_date: moment(payload.start_date).format('YYYY-MM-DD'),
      end_date: moment(payload.end_date).format('YYYY-MM-DD'),
    })
  }

  return (
    <div className="main-page-container !overflow-y-auto scrollbar">
      <div className="flex items-center justify-between">
        <PageHeader
          title={t('PayerReport')}
          iconSize={20}
          Icon={i18n.language === 'en' ? FaArrowCircleLeft : FaArrowCircleRight}
          onIconClick={() => navigate(-1)}
        />
      </div>

      <div className="flex items-center w-full justify-between gap-2 mb-2">
        <PayerReportFilters
          payloadData={payloadData}
          setPayloadData={setPayloadData}
          requestReport={requestReport}
        />
        <div className="flex gap-2">
          <CustomButton
            label={t('Export to Excel')}
            buttonType="success"
            icon={FaFileExcel}
            className="w-fit h-[35px]"
            onClick={() => exportToExcel(groupPayers(requestData?.data), t)}
          />
          <CustomButton
            label={t('Export to PDF')}
            buttonType="danger"
            icon={FaFilePdf}
            className="w-fit h-[35px]"
            onClick={() =>
              exportToPDF(groupPayers(requestData?.data), t, {
                ...payloadData,
                business_en: auth?.buisnessInfo?.name_en,
                business_ar: auth?.buisnessInfo?.name_ar,
                address: auth?.buisnessInfo?.address,
                logo: auth?.buisnessInfo?.logo,
              })
            }
          />
        </div>
      </div>

      <Table
        data={groupPayers(requestData?.data)}
        columns={columns}
        loading={loading}
        showGridlines
        rowClassName={rowData => {
          if (rowData?.isTotal) return '!bg-gray-100 font-bold'
          if (rowData?.isSaperator) return ''
          return ''
        }}
      />
    </div>
  )
}

export default PayerReport
